/* eslint-disable import/extensions */
/* eslint-disable no-lonely-if */
import { html, render } from "lit-html";
import { repeat } from "lit-html/directives/repeat.js";
import { classMap } from "lit-html/directives/class-map.js";
import { Component } from "@kluntje/core";
import MediaQueryService from "Services/MediaQueryService";
import { I18nService } from "Services/I18nService";
import { scrollLock } from "Helper/domHelper";
import { autobind } from "Helper/functionHelper/decorators";
import { createRef, ref } from "Helper/litHelpers";
import { ACTIVE } from "Helper/cssClasses";
import { toggleClass, hasClass, onEvent } from "@kluntje/js-utils/lib/dom-helpers";

import Month from "./Month";
import FraDatepickerField from "./fra-e-datetime-picker__field";
import {
  getDayNames,
  getLocaleWeekStartOffset,
  toDateString,
  toDateTimeString,
  toLocaleDateString,
  toTimeString,
} from "./dateUtils";
import fraEDatetimePickerScss from "./fra-e-datetime-picker.shadow.scss";

// .state of an FraDateTimePicker instance. see initialStates in ctor for more info
interface FraDateTimePickerState {
  open: boolean;
  startDate: string;
  startTime: string;
  hoverStart: string;
  endDate: string;
  endTime: string;
  hoverEnd: string;
  selectedDate: string;
  selectedTime: string;
  hoverSelectedDate: string;
  lastMonthToRender: string;
  firstMonthToRender: string;
  lastChanged: "" | "START" | "END";
  interactedWithEndTime: boolean;
}

// custom element to render a date and time picker where the user is able to choose a date (or two dates for start, end) from a calendar UI and also times (hour and minute from a fixed list of options)
class FraDateTimePicker extends Component {
  // round the time to the next 00, 15, 30, 45 min on the clock
  static roundTo15Min(date: Date | string | null) {
    if (date === null || date === "") return null;

    if (typeof date === "string") date = new Date(date);
    const SECONDS = 1000; // ms
    const MINUTES = 60 * SECONDS;
    // according to the user story, the user can choose between 00, 15, 30, 45 minutes
    const MIN_MINUTES = 15 * MINUTES;

    // round to next nearest 15 min time
    const newDate = new Date(Math.ceil(date.getTime() / MIN_MINUTES) * MIN_MINUTES);

    // in case of 00:00 jump to 00:15 because apparently users were confused
    if (newDate.getHours() === 0 && newDate.getMinutes() === 0) newDate.setMinutes(15);

    return newDate;
  }

  // tracks if the last user interaction was tab navigation, (will be false when last interaction was a click)
  private _hasTabNavigated = false;

  // the dialog was opened in MQ 1 or 2 which is full viewport. and needs scroll restriction on the body e.g.
  private _fullVpDialogOpened = false;

  // generate timeslots for header once dictionary is available
  private timeSlots: Array<{ date: string; label: string }> = [];

  constructor() {
    super({
      useShadowDOM: true,
      reactions: {
        "*": ["render"],
        open: ["updateGlobalEventListeners", "togglePageScrollLock"],
        selectedDate: ["updateDisplayValues"],
        selectedTime: ["updateDisplayValues"],
        startDate: ["updateDisplayValues"],
        startTime: ["updateDisplayValues"],
        endDate: ["updateDisplayValues"],
        endTime: ["updateDisplayValues"],
      },

      initialStates: {
        open: false,
        // for range selection, current start date in the format of "YYYY-MM-DD" (the time part hh:mm is in the `state.startTime`)
        // the value can be the currently marked date BEFORE the datepicker dialog is applied or canceled
        startDate: "",
        startTime: "00:00",
        // the date "YYYY-MM-DD" format of the day in the calendar which the user is currently hovering over and clicking it would mean it will be the new start day
        hoverStart: "",
        // similar to the `start` state but for the end date
        endDate: "",
        endTime: "23:45",
        // similar to the `hoverStart` state but for the end date
        hoverEnd: "",
        // similar to the `start` state but for the datepicker in normal (not range) variation
        selectedDate: "",
        selectedTime: "00:00",
        // similar to the `hoverStart` state but for the non range datepicker
        hoverSelectedDate: "",
        // months in the far future in the calendar ui will be rendered on demand, meaning when the user swipes past the current months, the one after etc the next ones will be rendered
        // this state tracks the last month needed to be currently rendered depending of the user's advancing position
        lastMonthToRender: "",
        // similar to `firstMonthToRender` state but for the earliest month
        firstMonthToRender: "",
        // "" | "START" | "END". user last interaction was with the start (date, time) or the end UI input elements.
        lastChanged: "",
        // user has interacted with the timepicker for the end date
        interactedWithEndTime: false,
      },
    });
  }

  /**
   * @override
   */
  afterComponentRender() {
    // Component event doesn't support event binding to light dom content
    onEvent(this.fields, "click", this.open, this);
    onEvent(this.fields, "focus", this.open, this);
    if (!this.fieldContainer) return;
    // support event binding field container including  icons
    onEvent(this.fieldContainer, "focus", this.open, this);
    onEvent(this.fieldContainer, "click", this.open, this);
  }

  updateGlobalEventListeners({ open }: FraDateTimePickerState) {
    if (open) {
      window.addEventListener("keydown", this.handleKeypress);
      if (MediaQueryService.currentMQ <= "MQ2" && !this.isFlightSearchVariant) {
        // allow closing the datepicker dialog with the back button (a pattern usually used on mobile devices)
        window.history.pushState({}, "");
        window.addEventListener("popstate", this.close);
      }
    } else {
      window.removeEventListener("keydown", this.handleKeypress);
      window.removeEventListener("popstate", this.close);
    }
  }

  // prevent user to scroll the content when the fullview dialog is open on mobile and user might swipe on it
  togglePageScrollLock({ open }: FraDateTimePickerState) {
    if (open && MediaQueryService.currentMQ <= "MQ2" && !this.isFlightSearchVariant) {
      this._fullVpDialogOpened = true;
      scrollLock.lock();
    }
    // closing mobile dialog
    else if (this._fullVpDialogOpened) {
      scrollLock.unLock();
      this._fullVpDialogOpened = false;
    }
  }

  open() {
    // even when the user has opened the UI via tab, he hasn't tabbed inside the UI yet!
    this._hasTabNavigated = false;

    let firstMonthToRender;
    // month for selected date / starts of selected range. assuming it is not after the min
    if (this.date || this.start) firstMonthToRender = new Month(this.date || this.start);
    // current month
    else if (!this.min) firstMonthToRender = new Month();
    else {
      const minMonth = this.minMonth!;
      const currentMonth = new Month();
      // today or min, which is later
      firstMonthToRender = minMonth > currentMonth ? minMonth : currentMonth;
    }

    // 1+3 months will fill twice the UI before lazy-rendering is needed
    const threeMonthAfterMin = firstMonthToRender.clone().change(+3);
    /* eslint-disable one-var */
    let lastMonthToRender;
    if (this.max) {
      const maxMonth = this.maxMonth!;
      // but not after the max date
      lastMonthToRender = maxMonth < threeMonthAfterMin ? maxMonth : threeMonthAfterMin;
    } else {
      lastMonthToRender = threeMonthAfterMin;
    }

    // should render at least two months to fill the UI
    if (lastMonthToRender.equals(firstMonthToRender)) lastMonthToRender.change(+1);

    let start = this.start;
    let selected = this.date;
    let end = this.end;
    if (this.isDateTime) {
      start = toDateTimeString(FraDateTimePicker.roundTo15Min(start));
      selected = toDateTimeString(FraDateTimePicker.roundTo15Min(selected));
      end = toDateTimeString(FraDateTimePicker.roundTo15Min(end));
    }

    this.setState({
      open: true,
      // remove any time parts
      startDate: toDateString(start),
      startTime: toTimeString(start),
      hoverStart: "",
      endDate: toDateString(end),
      endTime: toTimeString(end),
      hoverEnd: "",
      selectedDate: toDateString(selected),
      selectedTime: toTimeString(selected),
      hoverSelectedDate: "",
      lastMonthToRender: lastMonthToRender.toString(),
      firstMonthToRender: firstMonthToRender.toString(),
      lastChanged: "",
    });

    // says "last mq" but is the current MQ
    if (MediaQueryService.currentMQ >= "MQ3" || this.isFlightSearchVariant) {
      // if dialog opened out of view, scroll to center it
      const dialogEl = this.refs.dialog.value!;
      const { bottom, top } = dialogEl.getBoundingClientRect();
      if (bottom > window.innerHeight || top < 0) {
        // wait till the month scrolls has been applied. can't scroll the page when some other container is scrolling as well.
        setTimeout(() => dialogEl.scrollIntoView({ block: "center", behavior: "smooth" }), 0);
      }
    }
  }

  @autobind
  close(e: Event) {
    // make sure the created history entry is removed.
    // also helps to restore the scroll position on iOS, where the body is also overflow set to hidden
    if (e.type !== "popstate" && this._fullVpDialogOpened) window.history.back();

    this.setState({
      open: false,
      // reaction will use these to update the text fields
      startDate: toDateString(this.start),
      startTime: toTimeString(this.start),
      endDate: toDateString(this.end),
      endTime: toTimeString(this.end),
      selectedDate: toDateString(this.date),
      selectedTime: toTimeString(this.date),
    });
  }

  // intersection observer checking when the suer scroll/swipes to the start/end of rendered months in the calendar
  io?: IntersectionObserver;

  /**
   * when the user scrolls to the first or last month in the datepicker UI, render further months till the min/max boundaries are reached,
   * in that case disable the desktop prev/next navigation buttons
   *
   * @param {IntersectionObserverEntry[]} entries
   */
  @autobind
  intersectionCallback(entries: IntersectionObserverEntry[]) {
    entries.forEach(entry => {
      const isFirstRenderedMonth = entry.target === this.refs.firstMonth.value;
      const isLastRenderedMonth = entry.target === this.refs.lastMonth.value;

      if (entry.isIntersecting) {
        // first
        if (isFirstRenderedMonth) {
          let month = new Month(this.state.firstMonthToRender);
          const minMonth = this.minMonth;
          // already rendered the min allowed month
          if (minMonth && month <= minMonth) {
            this.refs.prevBtn.value!.disabled = true;
            return;
          }
          // use the currently first month element in the datepicker to update scroll position after other element are added to DOM
          const firstRenderedMonth = this.refs.firstMonth.value;
          // render previous four months (desktop UI shows always two months together)
          month.change(-4);
          // don't exceed the min allowed month
          if (minMonth && month < minMonth) month = minMonth;

          this.setState({ firstMonthToRender: month.toString() });
          // subtract margins from the offset, to align the margin start with the scroll container and not the month box itself
          // using `getComputedStyle` till `computedStyleMap` is supported everywhere and no need to remove the unit part from the value
          const left = firstRenderedMonth.offsetLeft - parseInt(getComputedStyle(firstRenderedMonth).marginLeft, 10);
          const top = firstRenderedMonth.offsetTop - parseInt(getComputedStyle(firstRenderedMonth).marginTop, 10);

          // restore scroll position
          this.refs.scrollContainer.value.scrollLeft = left;
          this.refs.scrollContainer.value.scrollTop = top;
        }
        // last
        else if (isLastRenderedMonth) {
          let month = new Month(this.state.lastMonthToRender);
          const maxMonth = this.maxMonth;
          // already rendered the max allowed month
          if (maxMonth && month >= maxMonth) {
            this.refs.nextBtn.value!.disabled = true;
            return;
          }

          // render next four months
          month.change(+4);
          // don't exceed the max allowed month
          if (maxMonth && month > maxMonth) month = maxMonth;

          const scrollLeft = this.refs.scrollContainer.value.scrollLeft;
          const scrollTop = this.refs.scrollContainer.value.scrollTop;

          this.setState({ lastMonthToRender: month.toString() });
          // make sure after the markup change the scroll left/top is still the same
          // I assume browser tries to apply the scroll position as percentage to the new container width rather than maintain its value
          this.refs.scrollContainer.value.scrollLeft = scrollLeft;
          this.refs.scrollContainer.value.scrollTop = scrollTop;
        }
      }
      // not in scroll view, enable buttons
      else {
        // first month not in view
        if (isFirstRenderedMonth) {
          this.refs.prevBtn.value!.disabled = false;
        }
        // last month not in view
        else if (isLastRenderedMonth) {
          this.refs.nextBtn.value!.disabled = false;
        }
      }
    });
  }

  // reference to rendered UI elements and callback for when they are rendered
  refs = {
    dialog: createRef<HTMLDivElement>(),
    scrollContainer: ((el: HTMLDivElement) => {
      /* eslint-disable no-invalid-this */
      this.refs.scrollContainer.value = el;
      this.io = new IntersectionObserver(this.intersectionCallback, {
        root: el,
        // when fully visible (or almost, to get around some rounding issues when 1-2px aren't visible),
        // so there is no re-render in the middle of the smooth scroll process
        threshold: 0.95,
      });
    }) as { (val: HTMLDivElement): void; value: HTMLDivElement },
    firstMonth: ((el: HTMLDivElement) => {
      this.refs.firstMonth.value = el;
      this.io!.observe(el);
    }) as { (val: HTMLDivElement): void; value: HTMLDivElement },
    lastMonth: ((el: HTMLDivElement) => {
      this.refs.lastMonth.value = el;
      this.io!.observe(el);
    }) as { (val: HTMLDivElement): void; value: HTMLDivElement },
    prevBtn: createRef<HTMLButtonElement>(),
    nextBtn: createRef<HTMLButtonElement>(),
    startHour: createRef<HTMLInputElement>(),
    startMinute: createRef<HTMLInputElement>(),
    endHour: createRef<HTMLInputElement>(),
    endMinute: createRef<HTMLInputElement>(),
    headerButtons: createRef<HTMLInputElement>(),
  };

  // renders the markup for the datepicker including the calendar UI
  render() {
    const { open, selectedTime, startTime, endTime, lastChanged }: FraDateTimePickerState = this.state;

    if (!open) {
      render(
        html`
          <style>
            ${fraEDatetimePickerScss}
          </style>
          <slot></slot>
        `,
        this.shadowRoot!,
      );
      return;
    }

    // remove any old observers, new ones will be added to the new first/last rendered month
    this.io?.disconnect();

    // assuming at this point the dictionary is loaded
    const saveLabel = I18nService.getInstance().getKey("fraport.travel.components.flightBooking.button.takeover.label");
    const closeLabel = I18nService.getInstance().getKey("fraport.travel.components.flightBooking.button.close.label");

    render(
      html`
        <style>
          ${fraEDatetimePickerScss}
        </style>
        <slot></slot>
        <div part="back-drop" class="fra-e-datetime-picker__back-drop" @click=${this.close}></div>
        <div
          part="dialog"
          class="fra-e-datetime-picker__dialog ${classMap({
            "fra-e-datetime-picker__dialog--has-range": this.isRange,
            "fra-e-datetime-picker__dialog--has-time": this.isDateTime,
            "fra-e-datetime-picker__dialog--has-header": this.isFlightSearchVariant,
            "fra-e-datetime-picker__dialog--has-error": !this.validity.valid,
          })}"
          role="application"
          aria-roledescription="datepicker"
          aria-label="Datepicker"
          @mousedown=${() => (this._hasTabNavigated = false)}
          ref=${ref(this.refs.dialog)}
        >
          <button class="fra-e-datetime-picker__close-btn" aria-label="cancel" @click=${this.close}>
            <fra-e-icon class="fra-e-icon" icon-id="fra-icon-close"></fra-e-icon>
          </button>
          <div class="fra-e-datetime-picker__date-header" ref=${ref(this.refs.headerButtons)}>
            ${this.generateHeader()}
          </div>
          <div class="fra-e-datetime-picker__container">
            <div class="fra-e-datetime-picker__scroll-container-wrapper">
              <div class="fra-e-datetime-picker__scroll-container" ref=${ref(this.refs.scrollContainer)}>
                ${repeat(
                  this.monthsMarkup(),
                  ({ key }) => key,
                  ({ markup }) => markup,
                )}
              </div>
            </div>

            <button
              class="fra-e-datetime-picker__nav-btn fra-e-datetime-picker__nav-btn--prev"
              @click=${this.scrollToPrevMonth}
              ref=${ref(this.refs.prevBtn)}
            >
              <fra-e-icon class="fra-e-icon" icon-id="fra-icon-chevron-left"></fra-e-icon>
            </button>
            <button
              class="fra-e-datetime-picker__nav-btn fra-e-datetime-picker__nav-btn--next"
              @click=${this.scrollToNextMonth}
              ref=${ref(this.refs.nextBtn)}
            >
              <fra-e-icon class="fra-e-icon" icon-id="fra-icon-chevron-right"></fra-e-icon>
            </button>
          </div>
          <footer class="fra-e-datetime-picker__footer">
            <div class="fra-e-datetime-picker__footer-content">
              ${this.isDateTime
                ? html`
                    <div class="fra-e-datetime-picker__times-wrapper">
                      ${this.timePicker({
                        label: this.fields[0].timeLabel,
                        time: startTime || selectedTime,
                        error: this.validity.endTimeBeforeStartTime && lastChanged === "START",
                        onChange: (time: string) =>
                          this.setState({
                            [this.isRange ? "startTime" : "selectedTime"]: time,
                            lastChanged: "START",
                          }),
                      })}
                      ${this.isRange
                        ? this.timePicker({
                            label: this.fields[1].timeLabel,
                            time: endTime,
                            error: this.validity.endTimeBeforeStartTime && lastChanged === "END",
                            onChange: (time: string) =>
                              this.setState({
                                endTime: time,
                                interactedWithEndTime: true,
                                lastChanged: "END",
                              }),
                          })
                        : ""}
                    </div>
                  `
                : ""}

              <div class="fra-e-datetime-picker__error-msg ${this.validity.errorMessage ? "fra-state-active" : ""}">
                ${this.validity.errorMessage}
              </div>

              <div class="fra-e-datetime-picker__footer-actions">
                <button class="fra-e-datetime-picker__cancel-btn" @click=${this.close}>${closeLabel}</button>

                <button
                  class="fra-e-datetime-picker__save-btn"
                  @click=${this.saveChanges}
                  ?disabled=${!this.validity.valid}
                >
                  ${saveLabel}
                </button>
              </div>
            </div>
          </footer>
        </div>
      `,
      this.shadowRoot!,
    );
  }

  private generateHeader() {
    if (!this.isFlightSearchVariant) return;

    this.timeSlots = FraDatepickerField.timeSlots();

    return this.timeSlots.map(
      slot => html`
        <button
          class="fra-e-datetime-picker__header-btn ${slot.date === this.state.selectedDate ? "fra-state-active" : ""}"
          data-day-id="${slot.date}"
          @click=${(e: Event) => this.switchDate(e)}
        >
          ${slot.label}
        </button>
      `,
    );
  }

  switchDate(e: Event): void {
    e.preventDefault();
    const selectedDay = e.target as HTMLButtonElement;
    const headerButtons = Array.from(this.refs.headerButtons.value?.children as HTMLCollection);
    const findActive = headerButtons.find(el => hasClass(el, ACTIVE)) as HTMLButtonElement;
    if (findActive) {
      toggleClass(findActive, ACTIVE);
    }
    toggleClass(selectedDay, ACTIVE);
    this.scrollToCurrMonth();

    this.setState({ selectedDate: selectedDay.dataset.dayId });
  }

  get headerButtons() {
    return this.querySelector(".fra-e-datetime-picker__header-btn");
  }

  // return markup for all months which should be rendered in the calendar ui
  private *monthsMarkup() {
    const { firstMonthToRender, lastMonthToRender } = this.state as FraDateTimePickerState;

    const firstMonth = new Month(firstMonthToRender);
    const lastMonth = new Month(lastMonthToRender);
    const month = firstMonth.clone();

    /* eslint-disable no-unmodified-loop-condition */
    // go from first month to last month, one month at a time and return the markup for the calendar UI for it
    do {
      const firstDay = month.firstDay;
      const monthAndYear = month.toLocaleString();

      // JS Date start with sunday,
      // so the index is based on Sunday being at index 0,
      // for german, subtract one to get it for when Mondays is at index zero (instead of the actual 1)
      // to prevent going below zero, do + n % n
      const firstDayOffset = (firstDay.getDay() - getLocaleWeekStartOffset() + 7) % 7;

      /* eslint-disable no-nested-ternary */
      const monthRef = month.equals(firstMonth)
        ? this.refs.firstMonth
        : month.equals(lastMonth)
          ? this.refs.lastMonth
          : // wont be used
            createRef();

      yield {
        // key is used for the re-rendering to not override already rendered months. only update their days if needed
        key: month.toString(),
        markup: html`
          <div class="fra-e-datetime-picker__month" ref=${ref(monthRef)}>
            <div class="fra-e-datetime-picker__month-name">${monthAndYear}</div>
            <div class="fra-e-datetime-picker__days-grid" style="--first-day-offset: ${firstDayOffset}">
              ${getDayNames().map(
                ({ name, abbr }) => html`
                  <span class="fra-e-datetime-picker__week-day" title=${name}>${abbr}</span>
                `,
              )}
              ${month.days.map(dayData => this.dayElement(dayData))}
            </div>
          </div>
        `,
      };

      month.change(+1);
    } while (month <= lastMonth);
  }

  // returns markup of one day element in the calendar UI
  private dayElement(dateObject: Date) {
    const { selectedDate, startDate, endDate, hoverSelectedDate, hoverStart, hoverEnd } = this
      .state as FraDateTimePickerState;
    const dateString = toDateString(dateObject);
    // remove any time part
    const minDateString = toDateString(this.min);
    const maxDateString = toDateString(this.max);

    /** "YYYY-MM-DD" */
    const date = dateString;
    const day = dateObject.getDate();
    const isToday = dateString === toDateString(new Date());
    // selected | start without end set yet
    const isSelected =
      dateString === (hoverSelectedDate || selectedDate) ||
      (dateString === (hoverStart || startDate) && (hoverEnd || endDate) < (hoverStart || startDate));
    const wasSelected =
      // selected date
      (dateString === selectedDate && hoverSelectedDate && dateString !== hoverSelectedDate) ||
      // selected start when user hovers before it
      (hoverStart && hoverStart < startDate && dateString === startDate) ||
      // selected end
      (hoverEnd && hoverEnd !== endDate && dateString === endDate);
    const isStart = dateString === (hoverStart || startDate) && (hoverStart || startDate) <= (hoverEnd || endDate);
    const isEnd = dateString === (hoverEnd || endDate) && (hoverStart || startDate) <= (hoverEnd || endDate);
    // between start and end; but do't highlight when the user is about to start selecting a range
    const isBetween = dateString > (hoverStart || startDate) && dateString < (hoverEnd || endDate);
    const canBeSelected =
      (!minDateString || dateString >= minDateString) && (!maxDateString || dateString <= maxDateString);

    const classes = {
      "fra-e-datetime-picker__day--today": isToday,
      "fra-e-datetime-picker__day--selected": isSelected,
      "fra-e-datetime-picker__day--was-selected": wasSelected,
      "fra-e-datetime-picker__day--start": isStart,
      "fra-e-datetime-picker__day--end": isEnd,
      "fra-e-datetime-picker__day--between": isBetween,
      "fra-e-datetime-picker__day--disabled": !canBeSelected,
    };

    return html`
      <button
        class="fra-e-datetime-picker__day ${classMap(classes)}"
        ?disabled=${!canBeSelected}
        tabindex="-1"
        data-date=${date}
        aria-current=${isToday ? "date" : "false"}
        aria-label=${toLocaleDateString(date)}
        aria-selected=${isSelected || isStart || isEnd ? "true" : "false"}
        @click=${() => {
          // normal selection
          if (!this.isRange) {
            this.setState({
              selectedDate: date,
            });
          }
          // range
          else {
            // set as range start on the first click after previously a range was set (in current use-cases there is always an initial value)
            // or when it is before the set start date
            if (date < startDate || (startDate && endDate)) {
              this.setState({
                startDate: date,
                endDate: "",
                lastChanged: "START",
              });
            } else {
              this.setState({
                endDate: date,
                lastChanged: "END",
              });
            }
          }
        }}
        @mouseenter=${() => {
          if (!this.isRange) {
            this.setState({
              hoverSelectedDate: date,
            });
          }
          // range
          // hovering for setting the start
          else if (date < startDate || (startDate && endDate)) {
            this.setState({
              hoverSelectedDate: date,
              hoverStart: "", // works as start / end not set, designer preferred selected range not be changed on hover for setting the start date
              hoverEnd: "",
            });
          }
          // hovering for setting the end
          else if (date > startDate) {
            this.setState({
              hoverEnd: date,
            });
          }
        }}
        @mouseleave=${() =>
          this.setState({
            hoverSelectedDate: "",
            hoverStart: "",
            hoverEnd: "",
          })}
      >
        ${day}
      </button>
    `;
  }

  // return markup for the hour or minute select dropdown element + increment/decrement buttons
  private timeDropDown({
    options,
    onChange,
  }: {
    options: Array<{ value: string; selected: boolean }>;
    onChange: Function;
  }) {
    const activeIndex = options.findIndex(el => el.selected);

    return html`
      <button
        class="fra-e-datetime-picker__time-nav"
        @click=${() => onChange(options[(activeIndex - 1 + options.length) % options.length].value)}
      >
        <fra-e-icon class="fra-e-icon" icon-id="fra-icon-chevron-left--bold"></fra-e-icon>
      </button>
      <div class="fra-e-datetime-picker__time-value">${options.find(({ selected }) => selected === true)!.value}</div>
      <button
        class="fra-e-datetime-picker__time-nav"
        @click=${() => onChange(options[(activeIndex + 1) % options.length].value)}
      >
        <fra-e-icon class="fra-e-icon" icon-id="fra-icon-chevron-right--bold"></fra-e-icon>
      </button>
    `;
  }

  // returns markup for the time picker. ( hour and minute drop down)
  timePicker({ label, time, onChange, error }: { label: string; time: string; onChange: Function; error?: boolean }) {
    const [hour, minute] = time.split(":");

    return html`
      <div class="fra-e-datetime-picker__time-fieldset-wrapper">
        <fieldset
          class="fra-e-datetime-picker__time-fieldset ${error ? "fra-e-datetime-picker__time-fieldset--error" : ""}"
        >
          <legend class="fra-e-datetime-picker__time-legend">${label}</legend>
          <select
            class="fra-e-datetime-picker__time-select"
            @change=${(e: InputEvent) => onChange((e.currentTarget as HTMLSelectElement).value)}
          >
            ${this.getTimeDropdownOptions(time).map(
              ({ value, selected, text }) => html`
                <option .selected=${selected} value=${value}>${text}</option>
              `,
            )}
          </select>
          <div class="fra-e-datetime-picker__clock">
            ${this.timeDropDown({
              options: this.getHours(hour),
              onChange: (hh: string) => onChange(`${hh}:${minute}`),
            })}
            <span class="fra-e-datetime-picker__time-separator">:</span>
            ${this.timeDropDown({
              options: this.getMinutes(minute),
              onChange: (mm: string) => onChange(`${hour}:${mm}`),
            })}
          </div>
        </fieldset>
      </div>
    `;
  }

  @autobind
  scrollToCurrMonth() {
    this.refs.scrollContainer.value.scrollTo({
      // desktop
      left: -this.refs.scrollContainer.value.scrollLeft,
      behavior: "smooth",
    });
  }

  @autobind
  scrollToPrevMonth() {
    this.refs.scrollContainer.value.scrollTo({
      // desktop
      left: this.refs.scrollContainer.value.scrollLeft - this.refs.firstMonth.value.clientWidth,
      behavior: "smooth",
    });
  }

  @autobind
  scrollToNextMonth() {
    this.refs.scrollContainer.value.scrollTo({
      // desktop
      left: this.refs.scrollContainer.value.scrollLeft + this.refs.firstMonth.value.clientWidth,
      behavior: "smooth",
    });
  }

  @autobind
  handleKeypress(e: KeyboardEvent) {
    const focusEl = this.shadowRoot!.activeElement as HTMLElement | null;

    if (e.code === "Escape") {
      // remove focus on first escape after a element was tab navigated to
      if (focusEl && this._hasTabNavigated) {
        focusEl.blur();
      }
      // close dialog on escape
      else {
        this.close(e);
      }
    } else if (e.code === "Tab") {
      this._hasTabNavigated = true;
      this.trapFocus(e);
    } else if (e.code === "Enter") {
      // not focused on any nav buttons / time dorpdown via tab navigation
      if (!this._hasTabNavigated || !focusEl?.matches(this.focusableChildSelectors)) {
        e.preventDefault();

        if (this.validity.valid) {
          this.saveChanges(e);
        }
      }
    }
  }

  /**
   * dialog is closed and the selected date(s) and times(s) are not reset.
   * @param {Event} e - click event or popstate event
   */
  @autobind
  saveChanges(e: Event) {
    // eslint-disable-next-line prefer-const
    let { startDate, startTime, endDate, endTime, selectedDate, selectedTime, interactedWithEndTime } = this.state;

    // user has only selected a start date for the range but no end date
    if (this.isRange && endDate === "") {
      // according to the user story, when only start date is selected, it will also be used for the end date.
      endDate = startDate;
      // and if the user hasn't interacted with the time picker then end time := start time +15min
      if (!interactedWithEndTime) {
        const date = new Date(`${startDate}T${startTime}`);
        date.setMinutes(date.getMinutes() + 15);
        // make sure 15min after start doesn't go beyond the max allowed date
        if (this.max && toDateString(date) > this.max) {
          date.setMinutes(date.getMinutes() - 15);
        }
        endDate = toDateString(date);
        endTime = toTimeString(date);
      }

      // update the state (for the validity and UI's potential error message)
      this.setState({
        endDate,
        endTime,
        lastChanged: "END",
      });
      // if using start date for the end date results to an invalid selection (end time before start time),
      // don't save the selection to the input fields
      if (!this.validity.valid) {
        return;
      }
    }

    if (selectedDate) {
      if (this.isDateTime) selectedDate += `T${selectedTime}`;
      this.date = selectedDate;
    }
    if (startDate) {
      if (this.isDateTime) startDate += `T${startTime}`;
      this.start = startDate;
    }
    if (endTime) {
      if (this.isDateTime) endDate += `T${endTime}`;
      this.end = endDate;
    }

    // i.e. closes the dialog and removes the history entry
    this.close(e);

    this.dispatchEvent(new CustomEvent("fra-e-datetime-picker:change"));
  }

  // returns any UI element in the datepicker where the user is supposed to be able to navigate with the keyboard to and interact with
  private get focusableChildSelectors() {
    // nav buttons (month, time), time dorp down (ignore close btn which is only visible on mobile)
    return 'button:not([tabindex="-1"]):not(:disabled):not(.fra-e-datetime-picker__close-btn), select';
  }

  // makes sure when the user uses the tab key to navigate, the focus never leavs the datepicker dialog and goes to other elements in the page. but rather starts at the tab beginning of the dialog
  trapFocus(e: KeyboardEvent) {
    const focusableElements = Array.from(this.shadowRoot!.querySelectorAll(this.focusableChildSelectors));
    const firstFocusableEl = focusableElements[0] as HTMLElement | undefined;
    const lastFocusableEl = focusableElements[focusableElements.length - 1] as HTMLElement | undefined;
    const currentFocusedElement = this.shadowRoot!.activeElement;
    if (e.shiftKey && (!currentFocusedElement || currentFocusedElement === firstFocusableEl)) {
      e.preventDefault();
      lastFocusableEl?.focus();
    } else if (!e.shiftKey && (!currentFocusedElement || currentFocusedElement === lastFocusableEl)) {
      e.preventDefault();
      firstFocusableEl?.focus();
    }
  }

  // shows the current selection in the date fields, without updating the "value"s
  updateDisplayValues({ startDate, startTime, endDate, endTime, selectedDate, selectedTime }: FraDateTimePickerState) {
    let selected = selectedDate;
    if (selectedDate && this.isDateTime) selected += `T${selectedTime}`;

    let start = startDate;
    if (startDate && this.isDateTime) start += `T${startTime}`;

    let end = endDate;
    if (endDate && this.isDateTime) end += `T${endTime}`;

    if (!this.isRange) this.fields[0].displayValue = toLocaleDateString(selected, this.isDateTime);
    else {
      this.fields[0].displayValue = toLocaleDateString(start, this.isDateTime);
      this.fields[1].displayValue = toLocaleDateString(end, this.isDateTime);
    }
  }

  // returns a list of hours in a day, values between 00 and 23, were the given hour is also marked as selected. To be used in the time drop down
  private getHours(h = "00") {
    return Array.from({ length: 24 })
      .fill(null)
      .map((_, ix) => {
        const value = String(ix).padStart(2, "0");
        return {
          value,
          selected: value === h,
        };
      });
  }

  // returns a list of minutes in an hour, values between 00 and 45, in 15 min steps, were the given time is also marked as selected. To be used in the time drop down
  private getMinutes(m = "00") {
    return Array.from({ length: 4 })
      .fill(null)
      .map((_, ix) => {
        const value = String(ix * 15).padStart(2, "0");

        return {
          value,
          selected: value === m,
        };
      });
  }

  /**
   * returns a list of time strings from '00:00' to '23:45' in 15' minutes steps
   *
   * @param {string} [time="00:00"] - current selected time e.g. from the components state. in the "hh:mm" format
   * @returns {Array}
   */
  private getTimeDropdownOptions(time = "00:00") {
    const options = [];

    for (let i = 0; i < 24; i++) {
      // according to the user story only 15 minutes steps are shown to the user
      for (let j = 0; j <= 45; j += 15) {
        // don't render 00:00 and jump to 00:15 because apparently users were confused
        if (i === 0 && j === 0) continue;

        const hh = String(i).padStart(2, "0");
        const mm = String(j).padStart(2, "0");
        const value = `${hh}:${mm}`;
        // design prefers a big gap between numbers and colon
        const text = html`
          ${hh}&emsp;:&emsp;${mm}
        `;
        options.push({
          value,
          text,
          selected: value === time,
        });
      }
    }

    return options;
  }

  /**
   * throws and error if the provide date sting can't be converted to a real value
   * @param {string} dateString - date in a format which is consumable by the `Date` constructor.
   *                              Empty string is viewed as valid
   * @throws
   */
  private validateDate(dateString: string) {
    // allow empty string for the date as there is no value, not selected etc. which is different than having a value which isn't representing a real date
    if (dateString === "") return;
    if (isNaN(new Date(dateString).getTime())) throw new TypeError(`invalid date passed! ${dateString}`);
  }

  /**
   * returns the month in which the field's `max` date falls into.
   * in case this date happens to be the very last day of a month, the following month will be the max month according to the user story
   * if there is no max restriction, `null` will be returned
   */
  private get maxMonth(): null | Month {
    if (this.max) {
      const maxDate = new Date(this.max);
      maxDate.setDate(maxDate.getDate() + 1);
      return new Month(maxDate);
    }

    return null;
  }

  /**
   * returns the month in which the field's `min` date falls into.
   * if there is no min restriction, `null` will be returned
   */
  private get minMonth(): null | Month {
    if (this.min) {
      return new Month(this.min);
    }

    return null;
  }

  /**
   * returns a list of one or two child fra-e-datetime-picker__field custom elements,
   * if a field element is set as disabled, that element will be ignored.
   */
  get fields() {
    return Array.from(this.querySelectorAll<FraDatepickerField>("fra-e-datetime-picker__field:not([disabled])"));
  }

  /**
   * returns container having date picker field & datepicker icon
   */
  get fieldContainer() {
    return this.querySelector(".fra-e-datetime-picker__container");
  }

  /**
   * date picker is meant to pick a start and end date
   */
  get isRange() {
    return this.fields.length === 2;
  }

  /**
   * user can choose a time in addition to a date on the datepicker,
   * for FlightSearchVariant- only date is supported
   */
  get isDateTime() {
    return this.isFlightSearchVariant ? false : this.fields[0].type === "datetime";
  }

  /**
   * checks if the date time picker is FlightSearch Variant
   */
  get isFlightSearchVariant() {
    return this.fields[0].variant === "flightsearch";
  }

  /**
   * will return the date / datetime of the selected date in the datepicker in the non range mode
   * @returns {string} in the "YYYY-MM-DD" format for type=date and "YYYY-MM-DDThh:mm" for datetime type
   */
  get date(): string {
    if (this.isRange) return "";

    return this.fields[0].value;
  }

  set date(value) {
    this.validateDate(value);
    this.fields[0].value = value;
  }

  /**
   * return the minimum date which the user is allowed to select in the datepciker to select.
   * even in case of range, the component spec assumes one min for both (start, end) dates
   * returns a string in the format of "YYYY-MM-DD" (there is no time restriction currently) when the field has this attribute
   * or `null` when there is no min restriction
   */
  get min() {
    return this.fields[0].min;
  }

  /**
   * similar to `min` getter but for the maximum date which is allowed to be selected in the datepicker
   */
  get max() {
    // even in case of range, the component spec assumes one min for both (start, end) dates
    return this.fields[0].max;
  }

  /**
   * will return the date / datetime of the first selected date in the datepicker in the range mode
   * @returns {string} in the "YYYY-MM-DD" format for type=date and "YYYY-MM-DDThh:mm" for datetime type, empty space when it is not a range datepicker, and null when no start date is selected yet.
   */
  get start() {
    if (this.isRange) return this.fields[0].value;
    return "";
  }

  /**
   * see the `start` getter doc for more information,
   * accepts a string in the format of "YYYY-MM-DD" format for type=date and "YYYY-MM-DDThh:mm" for datetime type
   * or empty string when there is no start value selected
   *
   * @param {string} value - start date
   */
  set start(value: string) {
    this.validateDate(value);
    this.fields[0].value = value;
  }

  /**
   * will return the date / datetime of the second selected date in the datepicker in the range mode
   * @returns {string} in the "YYYY-MM-DD" format for type=date and "YYYY-MM-DDThh:mm" for datetime type
   */
  get end() {
    if (this.isRange) return this.fields[1].value;
    return "";
  }

  /**
   * see `end` getter and `start` setter for more information
   *
   * @param {string} value - start date
   */
  set end(value: string) {
    this.validateDate(value);
    this.fields[1].value = value;
  }

  /**
   * checks if the user has selected the required values for date(s) and time(s)
   */
  get validity() {
    // eslint-disable-next-line prefer-const
    let { startDate, endDate, startTime, endTime } = this.state as FraDateTimePickerState;

    // according to the current user story and usage,
    // there are alway initial dates selected,
    // user might have not yet selected an end date, in that case designer didn't want any error message shown to the user (only after clicking the save btn)
    if (this.isRange && this.isDateTime && endDate && startDate === endDate && startTime >= endTime) {
      return {
        valid: false,
        endTimeBeforeStartTime: true,
        errorMessage: I18nService.getInstance().getKey(
          "fraport.travel.components.global.dateTimePicker.errorMessage.startTimeAfterEndTime",
        ),
      };
    }

    return {
      valid: true,
      errorMessage: "",
    };
  }
}

customElements.define("fra-e-datetime-picker", FraDateTimePicker);

export default FraDateTimePicker;
